<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Alert, Button, Toggle, Checkbox, Radio, Text, toast } from '@Shelly/shelly-design-system';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	//Stores
		//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	const id = real_id(device.id);

	const directionOptions = ['open', 'close', 'both'];
	const _directionOptionsI18n = [
		$format('option_on_opening') /**while opening*/,
		$format('option_on_closing') /**while closing*/,
		$format('option_on_moving') /**while moving*/,
	];
	const actionOptions = ['stop', 'pause', 'reverse'];
	const _actionOptionsI18n = [
		$format('option_stop') /** Stop (immediately stop on obstacle)*/,
		$format('option_pause'),
		$format('option_reverse') /** Reverse (stop and go back to fully open or close)*/,
	];
	const moveOptions = [null, 'reverse'];
	const _moveOptionsI18n = [
		$format('option_command_none') /** None*/,
		$format('option_reverse') /** Reverse*/,
	];

	let settings = $settingsStore,
		enable = settings[`cover:${device.channel}`].safety_switch.enable,
		action = settings[`cover:${device.channel}`].safety_switch.action,
		direction = settings[`cover:${device.channel}`].safety_switch.direction,
		allowedMove = settings[`cover:${device.channel}`].safety_switch.allowed_move;

	let cahceEnabled = enable;
	let cacheAction = action;
	let cacheDirection = direction;
	let cacheAllowed = allowedMove;
	let loading = false;

	const handleReq = async () => {
		if (
			cahceEnabled !== enable ||
			cacheAction !== action ||
			cacheDirection !== direction ||
			cacheAllowed !== allowedMove
		) {
			const params = {
				id: device.channel,
				config: {
					safety_switch: {
						action,
						direction,
						enable,
						allowed_move: allowedMove,
					},
				},
			};
			loading = true;
			let res: SettingRequest = await gen2Request(device, 'Cover.SetConfig', params);

			if (res.isok) {
				toast.open({
					type: 'success',
					title: $format('setting_scc_saved_toast'),
				});
				cahceEnabled = enable;
				cacheAction = action;
				cacheDirection = direction;
				cacheAllowed = allowedMove;
			} else {
				enable = cahceEnabled;
				action = cacheAction;
				direction = cacheDirection;
				allowedMove = cacheAllowed;
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			loading = false;
		}
	};
	const handleDirectionChange = (index) => {
		direction = directionOptions[index];
	};
	const handleActionChange = (index) => {
		action = actionOptions[index];
	};
	const handleMoveChange = (index) => {
		allowedMove = moveOptions[index];
	};

	const onToggleChange = () => {
		enable = !enable;
		handleReq();
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enable) {
			stickyBottomAction.set({ onClick: handleReq, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Toggle size="m" class="mb-s-plus" checked={enable} on:click={onToggleChange} id="checkbox-e1mhie7">
		{$format('label_enable')}
	</Toggle>
	<Alert
		class="mb-s-plus"
		heading={$format('safety_description_g2_info', {
			default:
				'This function works only if Button mode is set to : Single. Device must be controled from Input1, Input2 can be used as safety switch.',
		})}
	/>
	{#if enable}
		<Text styleType="body-small" class="mb-s">
			{$format('moving_direction') || 'Moving direction:'}
		</Text>
		{#each _directionOptionsI18n as option, index}
			<Radio
				size="m"
				class="mb-s"
				selected={directionOptions.indexOf(direction) === index}
				value={true}
				on:click={() => handleDirectionChange(index)}
				id="radio-syly9ws">{option}</Radio
			>
		{/each}

		<Text styleType="body-small" class="mt-s mb-s">{$format('type_action') + ':' || 'Action'}</Text>
		{#each _actionOptionsI18n as _action, index}
			<Radio
				class="mb-s"
				size="m"
				selected={actionOptions.indexOf(action) === index}
				value={true}
				disabled={index === 1 && allowedMove === null}
				on:click={() => handleActionChange(index)}
				id="radio-59esrye">{_action}</Radio
			>
		{/each}
		<Text styleType="body-small" class="mt-s mb-s">
			{$format('safety_switch_g2_allowed_move', { default: 'Allowed move:' })}
		</Text>
		{#each _moveOptionsI18n as move, index}
			<Radio
				class={classMap({
					'mb-s': index < _moveOptionsI18n.length - 1,
				})}
				size="m"
				selected={moveOptions.indexOf(allowedMove) === index}
				value={true}
				unavailable={moveOptions.indexOf(allowedMove) === index}
				disabled={index === 0 && action === 'reverse'}
				on:click={() => handleMoveChange(index)}
				id="radio-cvjmim0">{move}</Radio
			>
		{/each}
	{/if}
{:else}
	<Alert
		heading={$format('label_info')}
		isHeadingBold
		class="mb-s"
		message={$format('safety_description_g2', {
			default:
				'Attention: This function works only if Button mode is set to : Single. Device must be controled from Input1, Input2 can be used as safety switch.',
		})}
	/>
	<Checkbox size="m" class="mb-s" bind:checked={enable} id="checkbox-e1mhie7"
		>{$format('label_enable')}</Checkbox
	>
	<Text styleType="body-small" class="mb-s">
		{$format('moving_direction') || 'Moving direction:'}
	</Text>
	{#each _directionOptionsI18n as option, index}
		<Radio
			size="m"
			class="mb-s"
			selected={directionOptions.indexOf(direction) === index}
			value={true}
			on:click={() => handleDirectionChange(index)}
			id="radio-syly9ws">{option}</Radio
		>
	{/each}

	<Text styleType="body-small" class="mb-s">{$format('type_action') + ':' || 'Action'}</Text>
	{#each _actionOptionsI18n as _action, index}
		<Radio
			class="mb-s"
			size="m"
			selected={actionOptions.indexOf(action) === index}
			value={true}
			disabled={index === 1 && allowedMove === null}
			on:click={() => handleActionChange(index)}
			id="radio-59esrye">{_action}</Radio
		>
	{/each}
	<Text styleType="body-small" class="mb-s">
		{$format('safety_switch_g2_allowed_move', { default: 'Allowed move:' })}
	</Text>
	{#each _moveOptionsI18n as move, index}
		<Radio
			class="mb-s"
			size="m"
			selected={moveOptions.indexOf(allowedMove) === index}
			value={true}
			unavailable={moveOptions.indexOf(allowedMove) === index}
			disabled={index === 0 && action === 'reverse'}
			on:click={() => handleMoveChange(index)}
			id="radio-cvjmim0">{move}</Radio
		>
	{/each}
	<Button {loading} disabled={loading} size="m" width="full" on:click={handleReq}
		>{$format('button_save')}</Button
	>
{/if}
